import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../auth/supabase';
import './action-center.css';

function lineMedic(profile) {
  return String(profile?.member_kind || 'line_medic') === 'line_medic';
}

function ActionCard({ item }) {
  const open = async () => {
    try {
      await item.onOpen?.();
    } finally {
      window.location.assign(item.href);
    }
  };

  return (
    <button type="button" className={`action-center-card action-${item.level || 'normal'}`} onClick={open}>
      <span className="action-center-kicker">{item.kicker}</span>
      <strong>{item.title}</strong>
      <small>{item.detail}</small>
      <b>{item.cta || 'Open'} →</b>
    </button>
  );
}

export default function ActionCenter({ profile }) {
  const [data, setData] = useState({ posts: [], reads: [], acks: [], burns: [], signups: [], burnReads: [] });
  const [ready, setReady] = useState(false);

  const load = useCallback(async () => {
    if (!profile?.id || !lineMedic(profile)) return;
    try {
      const [postResult, readResult, ackResult, burnResult, signupResult, burnReadResult] = await Promise.all([
        supabase.rpc('member_list_program_feed_posts'),
        supabase.from('program_feed_reads').select('post_id,revision').eq('member_id', profile.id),
        supabase.from('program_feed_acknowledgments').select('post_id,revision').eq('member_id', profile.id),
        supabase.rpc('member_list_prescribed_burns'),
        supabase.from('prescribed_burn_signups').select('burn_id,status,selected_position').eq('member_id', profile.id),
        supabase.from('prescribed_burn_reads').select('burn_id,seen_updated_at,read_at').eq('member_id', profile.id),
      ]);
      if (postResult.error || readResult.error || ackResult.error || burnResult.error || signupResult.error || burnReadResult.error) return;
      setData({
        posts: (postResult.data || []).slice(0, 25),
        reads: readResult.data || [],
        acks: ackResult.data || [],
        burns: (burnResult.data || []).filter(burn => ['open_for_signup','signup_closed','roster_selected'].includes(burn.status)),
        signups: signupResult.data || [],
        burnReads: burnReadResult.data || [],
      });
      setReady(true);
    } catch (_) {
      // Keep the Action Center invisible if its 1.2 backend is unavailable.
    }
  }, [profile]);

  useEffect(() => { load(); }, [load]);

  const markBurnRead = useCallback(async burn => {
    if (!burn?.id || !profile?.id) return;
    const seenUpdatedAt = burn.updated_at || burn.published_at || new Date().toISOString();
    await supabase.from('prescribed_burn_reads').upsert({
      burn_id: burn.id,
      member_id: profile.id,
      seen_updated_at: seenUpdatedAt,
      read_at: new Date().toISOString(),
    }, { onConflict: 'burn_id,member_id' });
  }, [profile]);

  const actions = useMemo(() => {
    const items = [];

    if (profile && Object.prototype.hasOwnProperty.call(profile, 'mobile_verified_at') && !profile.mobile_verified_at) {
      items.push({ level: 'urgent', kicker: 'ACCOUNT SECURITY', title: 'Verify your mobile number', detail: 'Required for Line Medic 1.2 account recovery and security features.', href: '/settings', cta: 'Complete setup' });
    }

    const readKey = (id, revision) => data.reads.some(item => item.post_id === id && Number(item.revision) === Number(revision));
    const ackKey = (id, revision) => data.acks.some(item => item.post_id === id && Number(item.revision) === Number(revision));

    const requiredPost = data.posts.find(post => post.acknowledgment_required && !ackKey(post.id, post.revision));
    if (requiredPost) {
      items.push({ level: 'important', kicker: 'ACKNOWLEDGMENT REQUIRED', title: requiredPost.title, detail: 'Review this program update and acknowledge it.', href: `/program-feed/${requiredPost.id}`, cta: 'Review' });
    } else {
      const unreadPost = data.posts.find(post => !readKey(post.id, post.revision));
      if (unreadPost) items.push({ level: unreadPost.priority === 'urgent' ? 'urgent' : 'normal', kicker: 'NEW PROGRAM MESSAGE', title: unreadPost.title, detail: 'A new Program Feed message is waiting.', href: `/program-feed/${unreadPost.id}`, cta: 'Read message' });
    }

    const selectedSignup = data.signups.find(signup => ['selected','deployed'].includes(signup.status));
    const selectedBurn = selectedSignup ? data.burns.find(burn => burn.id === selectedSignup.burn_id) : null;
    if (selectedBurn) {
      items.push({ level: 'important', kicker: 'PRESCRIBED BURN', title: `Selected: ${selectedBurn.name}`, detail: selectedSignup.selected_position ? `Assignment: ${selectedSignup.selected_position}` : 'You were selected for this burn.', href: `/prescribed-burns/${selectedBurn.id}`, cta: selectedSignup.status === 'selected' ? 'Prepare assignment' : 'View assignment', onOpen: () => markBurnRead(selectedBurn) });
    } else {
      const openBurn = data.burns.find(burn => {
        if (burn.status !== 'open_for_signup') return false;
        const signup = data.signups.find(item => item.burn_id === burn.id);
        if (signup?.status === 'signed_up') return false;
        const read = data.burnReads.find(item => item.burn_id === burn.id);
        if (!read) return true;
        const seen = Date.parse(read.seen_updated_at || read.read_at || '');
        const current = Date.parse(burn.updated_at || burn.published_at || '');
        return Number.isFinite(current) && (!Number.isFinite(seen) || current > seen);
      });
      if (openBurn) items.push({ level: 'normal', kicker: 'SIGNUP OPEN', title: openBurn.name, detail: 'A prescribed burn is open for member signup.', href: `/prescribed-burns/${openBurn.id}`, cta: 'View burn', onOpen: () => markBurnRead(openBurn) });
    }

    return items.slice(0, 3);
  }, [data, profile, markBurnRead]);

  if (!lineMedic(profile) || !ready || !actions.length) return null;

  return (
    <section className="action-center">
      <div className="action-center-head"><div><span>ACTION CENTER</span><h2>Needs your attention</h2></div><small>{actions.length} active item{actions.length === 1 ? '' : 's'}</small></div>
      <div className="action-center-grid">{actions.map((item,index) => <ActionCard key={`${item.href}-${index}`} item={item} />)}</div>
    </section>
  );
}
